/**
 * Edpies Focus Mode — Seneca-inspired distraction-free learning shell.
 *
 * One reusable wrapper used by Challenger Series, Practice Papers, Mock Exams,
 * Learning Resources and any future question-flow surface. Hides the main app
 * chrome (nav, sidebars), centres the active card at 60–70% viewport on
 * desktop / full-width on mobile, and bakes in:
 *
 *   • progress bar + "Q X of Y" counter
 *   • optional countdown / count-up timer
 *   • next / previous controls
 *   • floating "Ask AI Tutor" button (slot)
 *   • accessibility controls: font size, dyslexia-friendly font, high
 *     contrast, dark mode (toggleable)
 *   • full keyboard navigation (Left/Right arrows, Enter to submit, Esc to
 *     exit)
 */

import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft, ArrowRight, Clock, X, Type, Eye, Moon, Sun, Accessibility, Pause, Play,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';

const FONT_SIZES = ['sm', 'md', 'lg', 'xl'];
const FONT_SIZE_CLASS = {
  sm: 'text-base md:text-lg',
  md: 'text-lg md:text-xl',
  lg: 'text-xl md:text-2xl',
  xl: 'text-2xl md:text-3xl',
};
// Bumped to v2 to flip the default theme from dark → light. Old key kept as
// a fallback so we don't lose users' a11y choices, just their theme.
const SETTINGS_KEY = 'edpies.focusMode.settings.v2';
const LEGACY_SETTINGS_KEY = 'edpies.focusMode.settings';
const DEFAULT_SETTINGS = { fontSize: 'md', dyslexia: false, highContrast: false, dark: false };

/** Hook: persist a11y / theme settings to localStorage. */
const useFocusSettings = () => {
  const [settings, setSettings] = useState(() => {
    try {
      const raw = localStorage.getItem(SETTINGS_KEY);
      if (raw) return { ...DEFAULT_SETTINGS, ...JSON.parse(raw) };
      // Migrate non-theme prefs from v1 once, force new light default.
      const legacy = localStorage.getItem(LEGACY_SETTINGS_KEY);
      if (legacy) {
        const parsed = JSON.parse(legacy);
        return {
          ...DEFAULT_SETTINGS,
          fontSize: parsed.fontSize || DEFAULT_SETTINGS.fontSize,
          dyslexia: !!parsed.dyslexia,
          highContrast: !!parsed.highContrast,
        };
      }
      return DEFAULT_SETTINGS;
    } catch {
      return DEFAULT_SETTINGS;
    }
  });
  useEffect(() => {
    try { localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings)); } catch { /* ignore */ }
  }, [settings]);
  const update = useCallback((patch) => setSettings((p) => ({ ...p, ...patch })), []);
  return [settings, update];
};

/** Format seconds as MM:SS (or HH:MM:SS if > 1h). */
const formatTime = (sec) => {
  if (sec == null || sec < 0) return '--:--';
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  const pad = (n) => String(n).padStart(2, '0');
  return h > 0 ? `${pad(h)}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`;
};

/** Estimate remaining time based on average pace so far. */
const estimateRemaining = (currentIdx, total, elapsedSec) => {
  if (!currentIdx || !total) return null;
  const avg = elapsedSec / Math.max(currentIdx, 1);
  const remainingQs = Math.max(total - currentIdx, 0);
  return Math.round(avg * remainingQs);
};

/**
 * Props
 * -----
 * title:           string — small label shown above the progress bar
 * current:         1-indexed current question number
 * total:           total question count
 * onPrev / onNext: handlers (controls hidden if undefined)
 * canPrev / canNext: enable/disable controls
 * onExit:          handler when the user clicks the X
 * timerMode:       'countdown' | 'countup' | null
 * timerSeconds:    countdown seconds remaining (for 'countdown'), or null
 * onTimeUp:        called when countdown reaches 0
 * tutorSlot:       ReactNode rendered as the floating Ask AI Tutor button + panel
 * children:        the question card / reading content
 * showEstimate:    bool — show "~X min left" estimate (default true)
 * progressLabel:   override for "Question X of Y" — e.g., "Page 1 of 3" or null to hide
 */
const FocusMode = ({
  title,
  current,
  total,
  onPrev,
  onNext,
  canPrev = true,
  canNext = true,
  onExit,
  timerMode = null,
  timerSeconds = null,
  onTimeUp,
  tutorSlot,
  children,
  showEstimate = true,
  progressLabel,
}) => {
  const navigate = useNavigate();
  const [settings, updateSettings] = useFocusSettings();
  const [showA11y, setShowA11y] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [paused, setPaused] = useState(false);
  const [countdownLeft, setCountdownLeft] = useState(timerSeconds ?? 0);

  // Sync countdown when parent updates timerSeconds.
  useEffect(() => {
    if (timerMode === 'countdown' && timerSeconds != null) {
      setCountdownLeft(timerSeconds);
    }
  }, [timerSeconds, timerMode]);

  // Tick the timer.
  useEffect(() => {
    if (paused) return undefined;
    const id = setInterval(() => {
      setElapsed((e) => e + 1);
      if (timerMode === 'countdown') {
        setCountdownLeft((s) => {
          if (s <= 1) {
            if (onTimeUp) onTimeUp();
            return 0;
          }
          return s - 1;
        });
      }
    }, 1000);
    return () => clearInterval(id);
  }, [paused, timerMode, onTimeUp]);

  // Keyboard navigation: Left = prev, Right = next, Esc = exit.
  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'ArrowRight' && canNext && onNext) onNext();
      else if (e.key === 'ArrowLeft' && canPrev && onPrev) onPrev();
      else if (e.key === 'Escape' && onExit) onExit();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onPrev, onNext, onExit, canPrev, canNext]);

  const handleExit = () => {
    if (onExit) onExit();
    else navigate(-1);
  };

  const pct = total > 0 ? Math.round((current / total) * 100) : 0;
  const remainingEstimate = showEstimate ? estimateRemaining(current, total, elapsed) : null;

  const themeRoot = settings.highContrast
    ? 'bg-black text-white'
    : settings.dark
      ? 'bg-slate-950 text-slate-100'
      : 'bg-slate-50 text-slate-900';

  const cardClass = settings.highContrast
    ? 'bg-black border-2 border-white'
    : settings.dark
      ? 'bg-slate-900/60 backdrop-blur border border-slate-700'
      : 'bg-white border border-slate-200 shadow-xl';

  const fontClass = settings.dyslexia
    ? 'font-[\'OpenDyslexic\',\'Comic_Sans_MS\',sans-serif]'
    : '';

  return (
    <div
      className={`min-h-screen w-full ${themeRoot} ${fontClass} transition-colors ${settings.dark && !settings.highContrast ? 'dark' : ''}`}
      data-testid="focus-mode-root"
      role="main"
      aria-label={title || 'Focus mode'}
    >
      {/* Top bar — exit, progress, timer, accessibility */}
      <header className="sticky top-0 z-40 backdrop-blur-md bg-inherit/80 border-b border-current/10">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center gap-3">
          <button
            onClick={handleExit}
            className="p-2 rounded-lg hover:bg-current/10 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-500 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent"
            aria-label="Exit focus mode"
            data-testid="focus-exit-btn"
          >
            <X className="h-5 w-5" />
          </button>

          <div className="flex-1 min-w-0">
            {title && <p className="text-xs uppercase tracking-wider opacity-60 truncate">{title}</p>}
            <div className="flex items-center gap-3">
              {progressLabel !== null && (
                <p className="text-sm font-semibold whitespace-nowrap" data-testid="focus-progress-label">
                  {progressLabel || `Question ${current} of ${total}`}
                </p>
              )}
              <div className="flex-1 max-w-md">
                <Progress
                  value={pct}
                  className="h-2"
                  aria-label={`Progress: ${pct}% complete`}
                  data-testid="focus-progress-bar"
                />
              </div>
              <p className="text-xs opacity-70 whitespace-nowrap hidden sm:block" data-testid="focus-progress-pct">
                {pct}%
              </p>
            </div>
          </div>

          {timerMode && (
            <button
              onClick={() => setPaused((p) => !p)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg hover:bg-current/10 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-500"
              aria-label={paused ? 'Resume timer' : 'Pause timer'}
              data-testid="focus-timer"
            >
              {paused ? <Play className="h-4 w-4" /> : <Pause className="h-4 w-4" />}
              <Clock className="h-4 w-4 opacity-70" />
              <span className="text-sm font-mono font-semibold tabular-nums">
                {timerMode === 'countdown' ? formatTime(countdownLeft) : formatTime(elapsed)}
              </span>
            </button>
          )}

          <button
            onClick={() => setShowA11y((s) => !s)}
            className="p-2 rounded-lg hover:bg-current/10 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-500"
            aria-label="Accessibility options"
            data-testid="focus-a11y-btn"
            aria-expanded={showA11y}
          >
            <Accessibility className="h-5 w-5" />
          </button>
        </div>

        {/* A11y dropdown */}
        <AnimatePresence>
          {showA11y && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="border-t border-current/10 overflow-hidden"
              data-testid="focus-a11y-panel"
            >
              <div className="max-w-5xl mx-auto px-4 py-3 flex flex-wrap items-center gap-2 text-sm">
                <span className="text-xs uppercase tracking-wide opacity-60 mr-2">Reading aids:</span>

                {/* Font size */}
                <div className="inline-flex items-center gap-1 px-1 py-1 rounded-lg bg-current/5">
                  <Type className="h-4 w-4 ml-1 opacity-60" />
                  {FONT_SIZES.map((sz) => (
                    <button
                      key={sz}
                      onClick={() => updateSettings({ fontSize: sz })}
                      className={`px-2 py-0.5 rounded text-xs font-semibold uppercase focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-500 ${
                        settings.fontSize === sz ? 'bg-purple-600 text-white' : 'opacity-70 hover:opacity-100'
                      }`}
                      data-testid={`focus-a11y-font-${sz}`}
                      aria-pressed={settings.fontSize === sz}
                    >
                      {sz}
                    </button>
                  ))}
                </div>

                <button
                  onClick={() => updateSettings({ dyslexia: !settings.dyslexia })}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-500 ${
                    settings.dyslexia ? 'bg-purple-600 text-white' : 'bg-current/5 hover:bg-current/10'
                  }`}
                  data-testid="focus-a11y-dyslexia"
                  aria-pressed={settings.dyslexia}
                >
                  Dyslexia-friendly font
                </button>

                <button
                  onClick={() => updateSettings({ highContrast: !settings.highContrast })}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-500 ${
                    settings.highContrast ? 'bg-purple-600 text-white' : 'bg-current/5 hover:bg-current/10'
                  }`}
                  data-testid="focus-a11y-contrast"
                  aria-pressed={settings.highContrast}
                >
                  <Eye className="h-3.5 w-3.5" /> High contrast
                </button>

                <button
                  onClick={() => updateSettings({ dark: !settings.dark, highContrast: false })}
                  className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-current/5 hover:bg-current/10 flex items-center gap-1.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-500"
                  data-testid="focus-a11y-theme"
                  aria-label="Toggle dark / light mode"
                >
                  {settings.dark ? <Sun className="h-3.5 w-3.5" /> : <Moon className="h-3.5 w-3.5" />}
                  {settings.dark ? 'Light' : 'Dark'} mode
                </button>

                {remainingEstimate != null && (
                  <span className="ml-auto text-xs opacity-60">
                    ~{Math.ceil(remainingEstimate / 60)} min remaining
                  </span>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      {/* Main centred question card — 60–70% on desktop, full-width on mobile */}
      <main className="px-4 py-6 md:py-12 flex items-start md:items-center justify-center min-h-[calc(100vh-64px)]">
        <motion.div
          key={current}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.25 }}
          className={`w-full md:w-[70%] lg:w-[60%] max-w-3xl rounded-2xl ${cardClass} p-6 md:p-10 ${FONT_SIZE_CLASS[settings.fontSize]}`}
          data-testid="focus-question-card"
        >
          {children}
        </motion.div>
      </main>

      {/* Prev / Next controls — only render if at least one handler provided */}
      {(onPrev || onNext) && (
        <nav
          className="sticky bottom-0 z-30 backdrop-blur-md bg-inherit/80 border-t border-current/10"
          aria-label="Question navigation"
        >
          <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
            <Button
              variant="ghost"
              onClick={onPrev}
              disabled={!canPrev || !onPrev}
              className="gap-2"
              data-testid="focus-prev-btn"
            >
              <ArrowLeft className="h-4 w-4" />
              Previous
            </Button>
            <p className="text-xs opacity-60 hidden md:block">
              Use ← / → arrow keys to navigate · Esc to exit
            </p>
            <Button
              onClick={onNext}
              disabled={!canNext || !onNext}
              className="gap-2 bg-purple-600 hover:bg-purple-700 text-white"
              data-testid="focus-next-btn"
            >
              Next
              <ArrowRight className="h-4 w-4" />
            </Button>
          </div>
        </nav>
      )}

      {/* Floating Ask AI Tutor slot — slot pattern lets each surface plug in
          its own tutor flow without coupling this shell to one provider. */}
      {tutorSlot}
    </div>
  );
};

export default FocusMode;
